import { useGetFounderHome } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { ClipboardList, CheckSquare, AlertTriangle, TrendingUp, FileText, Video, Newspaper } from "lucide-react";
import InfoTip from "@/components/info-tip";
import AdvisorCard from "@/components/founder/advisor-card";

export default function FounderHome({ founderId, cohortSlug }: { founderId: number; cohortSlug?: string }) {
  const { data, isLoading, error } = useGetFounderHome(founderId);

  if (isLoading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
      <Skeleton className="h-48" />
    </div>
  );

  if (error || !data) return (
    <div className="p-8 text-center text-muted-foreground">Unable to load founder workspace</div>
  );

  const { founder, company, pendingApprovals, outstandingAsks, recentUpdates, companyHealthSummary } = data;

  return (
    <div className="space-y-8" data-testid="page-founder-home">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Welcome back, {founder.name.split(" ")[0]}</h1>
          <InfoTip content="This is your Founder Workspace — your personal hub for everything related to your company at Favcy. Submit weekly check-ins, approve what gets shared with investors, and see how your company profile looks to different audiences." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">{company?.name} &mdash; {company?.sector}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <CheckSquare className="w-3 h-3" /> Pending Approvals <InfoTip content="Items the ops team has flagged for your sign-off before they're shared with investors. You have full control over what goes external." />
            </div>
            <p className="text-2xl font-serif font-bold">{pendingApprovals}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <AlertTriangle className="w-3 h-3" /> Open Asks <InfoTip content="Requests from the Favcy ops team that need your attention — things like providing data, reviewing documents, or responding to investor questions." />
            </div>
            <p className="text-2xl font-serif font-bold">{outstandingAsks?.length || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <TrendingUp className="w-3 h-3" /> KPIs Tracked
            </div>
            <p className="text-2xl font-serif font-bold">{companyHealthSummary?.kpiCount || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <FileText className="w-3 h-3" /> Documents
            </div>
            <p className="text-2xl font-serif font-bold">{companyHealthSummary?.documentCount || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Phase 1.2 — Advisor card. Server filters to visible touches only;
          per PRD §16 Q6 we never expose breach/at_risk state to founders. */}
      <AdvisorCard theme="light" />

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-serif">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link href={cohortSlug ? `/c/${cohortSlug}/founder/${founderId}/checkin` : `/founder/${founderId}/checkin`}>
              <Button variant="outline" className="w-full justify-start">
                <ClipboardList className="w-4 h-4 mr-2" /> Submit Weekly Check-in
              </Button>
            </Link>
            <Link href={cohortSlug ? `/c/${cohortSlug}/founder/${founderId}/approvals` : `/founder/${founderId}/approvals`}>
              <Button variant="outline" className="w-full justify-start">
                <CheckSquare className="w-4 h-4 mr-2" /> Review Approvals
                {pendingApprovals > 0 && (
                  <Badge variant="default" className="ml-auto text-xs">{pendingApprovals}</Badge>
                )}
              </Button>
            </Link>
            <Link href={cohortSlug ? `/c/${cohortSlug}/founder/${founderId}/preview` : `/founder/${founderId}/preview`}>
              <Button variant="outline" className="w-full justify-start">
                <Newspaper className="w-4 h-4 mr-2" /> Preview Company Profile
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-serif">Outstanding Asks</CardTitle>
          </CardHeader>
          <CardContent>
            {!outstandingAsks?.length ? (
              <p className="text-sm text-muted-foreground">No outstanding asks</p>
            ) : (
              <div className="space-y-2">
                {outstandingAsks.map((ask: any) => (
                  <div key={ask.id} className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm font-medium">{ask.title}</p>
                    {ask.description && <p className="text-xs text-muted-foreground mt-1">{ask.description}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[10px]">{ask.priority}</Badge>
                      {ask.assignedTo && <span className="text-[10px] text-muted-foreground">Assigned to {ask.assignedTo}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-serif">Recent Updates</CardTitle>
        </CardHeader>
        <CardContent>
          {!recentUpdates?.length ? (
            <p className="text-sm text-muted-foreground">No updates yet. Submit your first weekly check-in!</p>
          ) : (
            <div className="space-y-3">
              {recentUpdates.map((update: any) => (
                <div key={update.id} className="flex items-start justify-between p-3 border rounded-md">
                  <div>
                    <p className="text-sm font-medium">{update.headline || "Weekly Update"}</p>
                    <p className="text-xs text-muted-foreground">
                      Week of {new Date(update.weekOf).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{update.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
